import React, { useRef, useEffect, useState, useCallback } from 'react';
import {
  Room,
  RoomEvent,
  RemoteTrackPublication,
  RemoteTrack,
  Track,
  Participant,
  RemoteParticipant,
  VideoQuality // Import VideoQuality
} from 'livekit-client';
import { FaMicrophone, FaMicrophoneSlash, FaVideo, FaVideoSlash, FaCog, FaVolumeUp, FaVolumeMute, FaPlay } from 'react-icons/fa'; // Import FaCog, FaVolumeUp, FaVolumeMute, FaPlay
import watermarkGif from './../../assets/exodium-blue.gif'; // Import the GIF
import { ReactionOverlay } from './ReactionOverlay.tsx'; // importar
import AudioWaveform from './AudioWaveform.tsx'; // Importar el nuevo componente
import AudioEqualizer, { EqualizerBand } from './AudioEqualizer.tsx';  // Update import to get the type

interface StreamPlayerProps {
  streamUrl: string | null;
  room: Room | null;
  isOwner: boolean;
  localStream: MediaStream | null;
  viewerCount: number;
  logPrefix: string;
  remoteMicMuted: boolean;
  remoteCamOff: boolean;
  ownerAddress: string | undefined; // Need owner address to find the right participant
  liveStartTime: number | null; // Add liveStartTime prop
  estimatedDuration?: number; // Add estimatedDuration prop
  isMicMuted?: boolean; // Add isMicMuted prop for owner
}

const StreamPlayer: React.FC<StreamPlayerProps> = ({
  streamUrl,
  room,
  isOwner,
  localStream,
  viewerCount,
  logPrefix,
  remoteMicMuted,
  remoteCamOff,
  ownerAddress, // Destructure ownerAddress
  liveStartTime, // Destructure liveStartTime
  estimatedDuration, // Destructure estimatedDuration
  isMicMuted = false, // Default to false if not provided
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [attachedTrackSid, setAttachedTrackSid] = useState<string | null>(null);
  const attachedPublicationRef = useRef<RemoteTrackPublication | null>(null); // Ref to store the publication
  const [desiredQuality, setDesiredQuality] = useState<VideoQuality>(VideoQuality.HIGH); // Default to HIGH
  const [showQualityMenu, setShowQualityMenu] = useState(false);
  const [volume, setVolume] = useState<number>(0.5); // <-- Set initial volume to 50%
  const remoteMicMutedRef = useRef(remoteMicMuted); // Ref to track previous remoteMicMuted state
  const [needsInteraction, setNeedsInteraction] = useState(false); // <-- New state for autoplay interaction
  const [isLocallyMuted, setIsLocallyMuted] = useState(false); // <-- New state for local mute toggle
  const [elapsed, setElapsed] = useState<number>(0); // New state for elapsed time
  const [audioTrack, setAudioTrack] = useState<MediaStreamTrack | null>(null);
  const [showEqualizer, setShowEqualizer] = useState<boolean>(false); // Nuevo estado para controlar la visualización del ecualizador
  // New state to store equalizer settings
  const [equalizerSettings, setEqualizerSettings] = useState<EqualizerBand[]>([
    { frequency: 60, gain: 0, label: 'Bass' },
    { frequency: 150, gain: 0, label: 'Low-Mid' },
    { frequency: 400, gain: 0, label: 'Mid' },
    { frequency: 1000, gain: 0, label: 'High-Mid' },
    { frequency: 2400, gain: 0, label: 'Presence' },
    { frequency: 6000, gain: 0, label: 'Treble' }
  ]);

  // Estable callback para aplicar calidad, enfocado en forzar calidad alta
  const applyVideoQuality = useCallback((quality: VideoQuality, publication: RemoteTrackPublication | null) => {
    if (publication && publication.kind === Track.Kind.Video && publication.track) {
      try {
        // Intentar desactivar adaptativeStream en la pista específica (si es posible)
        if (typeof publication.setVideoQuality === 'function') {
          console.log(logPrefix, `Forzando calidad de video a HIGH sin importar adaptiveStream`);
          
          // Intento directo con la API de LiveKit
          publication.setVideoQuality(VideoQuality.HIGH);
          
          // NUEVO: Intento alternativo accediendo al track directamente
          const track = publication.track;
          if (track && track.dimensions) {
            console.log(logPrefix, `Dimensiones actuales del track: ${track.dimensions.width}x${track.dimensions.height}`);
          }
          
          // NUEVO: Intento detectar llamada de optimización
          setTimeout(() => {
            if (track && track.dimensions) {
              console.log(logPrefix, `Dimensiones después de 1s: ${track.dimensions.width}x${track.dimensions.height}`);
            }
          }, 1000);
        } else {
          console.warn(logPrefix, `La publicación no tiene método setVideoQuality`);
        }
      } catch (e) {
        console.warn(logPrefix, 'setVideoQuality failed:', e);
      }
    } else {
      console.log(logPrefix, `Cannot apply quality, no valid publication provided.`);
    }
  }, [logPrefix]);

  // Al cambiar la pista adjunta, forzar HIGH como calidad inicial
  useEffect(() => {
    if (attachedPublicationRef.current) {
      applyVideoQuality(VideoQuality.HIGH, attachedPublicationRef.current);
    }
  }, [attachedTrackSid, applyVideoQuality]);

  // Stable callback to find and attach the owner's video track
  const findAndAttachOwnerVideoTrack = useCallback(() => {
    const videoElement = videoRef.current;
    console.log(logPrefix, "[AttachLogic] Running findAndAttachOwnerVideoTrack..."); // Log entry

    if (!videoElement || !room || !ownerAddress) {
        console.log(logPrefix, "[AttachLogic] Conditions not met (no videoElement, room, or ownerAddress). Detaching if needed.");
        if (videoElement && videoElement.srcObject) { // Check srcObject for detachment
            console.log(logPrefix, `[AttachLogic] Detaching previous tracks due to unmet conditions.`);
            // Stop tracks in the stream before clearing srcObject
            (videoElement.srcObject as MediaStream)?.getTracks().forEach(track => track.stop());
            videoElement.pause();
            videoElement.srcObject = null;
        }
        setAttachedTrackSid(null);
        attachedPublicationRef.current = null; // Keep track of the *video* publication for quality control
        return;
    }

    let videoTrackToAttach: RemoteTrack | null = null;
    let audioTrackToAttach: RemoteTrack | null = null; 
    let videoPublicationToAttach: RemoteTrackPublication | null = null;

    const ownerParticipant = room.getParticipantByIdentity(ownerAddress);

    if (ownerParticipant) {
        console.log(logPrefix, `[AttachLogic] Found owner participant (${ownerAddress}). Checking tracks...`);
        // --- ADDED LOGGING: Log all available video pubs ---
        const availableVideoPubs = Array.from(ownerParticipant.videoTrackPublications.values())
            .map(pub => ({
                sid: pub.trackSid,
                source: pub.source,
                subscribed: pub.isSubscribed,
                hasTrack: !!pub.track,
                trackMuted: pub.track?.isMuted ?? 'N/A', // Log track mute state if track exists
                pubMuted: pub.isMuted // Log publication mute state
            }));
        console.log(logPrefix, "[AttachLogic] Current owner video publications BEFORE selection:", JSON.stringify(availableVideoPubs));
        // --- END ADDED LOGGING ---

        // --- Prioritize ScreenShare Video ---
        for (const pub of ownerParticipant.videoTrackPublications.values()) {
            if (pub.source === Track.Source.ScreenShare && pub.isSubscribed && pub.track) {
                console.log(logPrefix, `[AttachLogic] Found SUITABLE ScreenShare video track: ${pub.trackSid}`);
                videoTrackToAttach = pub.track;
                videoPublicationToAttach = pub;
                break;
            }
        }

        // --- Fallback to Camera Video ---
        if (!videoTrackToAttach) {
            console.log(logPrefix, "[AttachLogic] No suitable ScreenShare found. Checking for Camera video track...");
            for (const pub of ownerParticipant.videoTrackPublications.values()) {
                if (pub.source === Track.Source.Camera && pub.isSubscribed && pub.track) {
                    console.log(logPrefix, `[AttachLogic] Found SUITABLE Camera video track: ${pub.trackSid}`);
                    videoTrackToAttach = pub.track;
                    videoPublicationToAttach = pub;
                    break;
                }
            }
        }

        // --- Find Audio Track ---
        const audioPub = Array.from(ownerParticipant.audioTrackPublications.values()).find(pub => pub.isSubscribed && pub.track);
        if (audioPub) {
            audioTrackToAttach = audioPub.track; // <-- Store the audio track
            console.log(logPrefix, `[AttachLogic] Found subscribed AUDIO track: SID=${audioPub.trackSid}, isMuted=${audioPub.isMuted}, trackEnabled=${audioTrackToAttach?.isEnabled}`);
        } else {
            console.log(logPrefix, `[AttachLogic] No subscribed AUDIO track found for owner.`);
        }
        // --- END Find Audio Track ---

    } else {
        console.log(logPrefix, "[AttachLogic] Owner participant not found in the room yet.");
    }

    // --- Decision Logic ---
    // We need at least a video track to proceed
    if (videoPublicationToAttach && videoTrackToAttach) {
        // Attach if it's a new video track or nothing is attached
        if (!attachedPublicationRef.current || attachedPublicationRef.current.trackSid !== videoPublicationToAttach.trackSid) {
            console.log(logPrefix, `[AttachLogic] Attaching NEW video track ${videoPublicationToAttach.trackSid} (Source: ${videoPublicationToAttach.source}). Audio track SID: ${audioTrackToAttach?.sid ?? 'None'}`);

            // Detach previous stream if exists
            if (videoElement.srcObject) {
                console.log(logPrefix, `[AttachLogic] Detaching previous stream tracks.`);
                // Stop tracks before clearing
                (videoElement.srcObject as MediaStream)?.getTracks().forEach(track => track.stop());
                videoElement.pause();
                videoElement.srcObject = null; // Explicitly clear
            }

            // --- Create new MediaStream and add tracks ---
            const newStream = new MediaStream();
            let addedVideoTrackId: string | null = null;
            let addedAudioTrackId: string | null = null;

            if (videoTrackToAttach.mediaStreamTrack) {
                newStream.addTrack(videoTrackToAttach.mediaStreamTrack);
                addedVideoTrackId = videoTrackToAttach.mediaStreamTrack.id;
                console.log(logPrefix, `[AttachLogic] Added video track (${addedVideoTrackId}) to new stream.`);
            } else {
                 console.warn(logPrefix, `[AttachLogic] Video track ${videoTrackToAttach.sid} has no mediaStreamTrack to add.`);
            }

            // MEJORA: Incluir el audio en el mismo stream de video para mejor sincronización
            if (audioTrackToAttach?.mediaStreamTrack) {
                newStream.addTrack(audioTrackToAttach.mediaStreamTrack);
                addedAudioTrackId = audioTrackToAttach.mediaStreamTrack.id;
                console.log(logPrefix, `[AttachLogic] Added audio track (${addedAudioTrackId}) to new stream.`);
            } else {
                 console.log(logPrefix, `[AttachLogic] No audio mediaStreamTrack available to add.`);
            }
            // --- ---

            /* COMENTADO: No usar audio separado para evitar desincronización
            // attach audio separately for low-latency playback
            if (audioTrackToAttach?.mediaStreamTrack && audioRef.current) {
              const audioStream = new MediaStream([audioTrackToAttach.mediaStreamTrack]);
              audioRef.current.srcObject = audioStream;
              audioRef.current.play().catch(e => console.warn(logPrefix, "Audio play failed:", e));
            }
            */

            // Attach new stream
            videoElement.srcObject = newStream;
            console.log(logPrefix, `[AttachLogic] Applying initial volume: ${volume} and muted state: true (forced for autoplay)`);
            videoElement.volume = volume;
            videoElement.muted = true;    // force muted for autoplay
            videoElement.playsInline = true;
            videoElement.load();

            videoElement.play().then(() => {
                console.log(logPrefix, `[AttachLogic] Autoplay started (muted).`);
            }).catch(e => {
                console.warn(logPrefix, "[AttachLogic] Autoplay still blocked:", e);
                setNeedsInteraction(true);
            });

            // Log srcObject state after attach
            if (videoElement.srcObject instanceof MediaStream) {
                const stream = videoElement.srcObject;
                const audioTracks = stream.getAudioTracks();
                const videoTracks = stream.getVideoTracks();
                console.log(logPrefix, `[AttachLogic] videoElement.srcObject is MediaStream. AudioTracks: ${audioTracks.length}, VideoTracks: ${videoTracks.length}. Track IDs: Audio=[${audioTracks.map(t=>t.id).join(', ')}], Video=[${videoTracks.map(t=>t.id).join(', ')}]`);
            } else {
                console.log(logPrefix, `[AttachLogic] videoElement.srcObject is NOT a MediaStream after attach.`);
            }

            console.log(logPrefix, `[AttachLogic] Attempting to play new stream with video track ${videoTrackToAttach.sid}...`);
            videoElement.play().then(() => {
                console.log(logPrefix, `[AttachLogic] Playback started for stream containing video track ${videoTrackToAttach.sid}.`);
            }).catch(e => {
                console.error(logPrefix, `[AttachLogic] Auto-play failed for stream containing video track ${videoTrackToAttach.sid}:`, e);
                // --- MODIFICATION START ---
                // Don't immediately require interaction on NotAllowedError here.
                // Let the useEffect sync logic handle potential persistent failures later.
                if (e.name === 'NotAllowedError') {
                    console.log(logPrefix, "[AttachLogic] Autoplay failed due to browser policy, but NOT setting needsInteraction immediately.");
                    // setNeedsInteraction(true); // REMOVED
                    // setIsLocallyMuted(true); // REMOVED
                }
                // --- MODIFICATION END ---
            });

            setAttachedTrackSid(videoPublicationToAttach.trackSid); // Still track the video SID
            attachedPublicationRef.current = videoPublicationToAttach; // Store the video pub for quality control
            applyVideoQuality(VideoQuality.HIGH, videoPublicationToAttach); // Forzar calidad HIGH siempre al adjuntar

        } else {
            console.log(logPrefix, `[AttachLogic] Video track ${videoPublicationToAttach.trackSid} is already attached. Ensuring playback, quality, volume, mute state, and audio track presence.`);
            // Ensure ref is correct
            if (attachedPublicationRef.current !== videoPublicationToAttach) {
                attachedPublicationRef.current = videoPublicationToAttach;
            }

            // --- Check if audio track needs to be added/removed from existing stream ---
            if (videoElement.srcObject instanceof MediaStream) {
                const currentStream = videoElement.srcObject;
                const currentAudioTracks = currentStream.getAudioTracks();
                const ownerAudioTrack = audioTrackToAttach?.mediaStreamTrack;

                if (ownerAudioTrack && currentAudioTracks.length === 0) {
                    console.log(logPrefix, `[AttachLogic] Adding missing audio track (${ownerAudioTrack.id}) to existing stream.`);
                    currentStream.addTrack(ownerAudioTrack);
                } else if (!ownerAudioTrack && currentAudioTracks.length > 0) {
                    console.log(logPrefix, `[AttachLogic] Removing extraneous audio track (${currentAudioTracks[0].id}) from existing stream.`);
                    currentStream.removeTrack(currentAudioTracks[0]);
                    // Optionally stop the removed track? LiveKit might handle this.
                    // currentAudioTracks[0].stop();
                } else if (ownerAudioTrack && currentAudioTracks.length > 0 && currentAudioTracks[0].id !== ownerAudioTrack.id) {
                    console.log(logPrefix, `[AttachLogic] Replacing audio track (${currentAudioTracks[0].id}) with new one (${ownerAudioTrack.id}) in existing stream.`);
                    currentStream.removeTrack(currentAudioTracks[0]);
                    // currentAudioTracks[0].stop();
                    currentStream.addTrack(ownerAudioTrack);
                }
            }
            // --- ---

            console.log(logPrefix, `[AttachLogic] Ensuring volume: ${volume} and muted state: ${remoteMicMuted || isLocallyMuted}`);
            videoElement.volume = volume;
            videoElement.muted = remoteMicMuted || isLocallyMuted;
            if (videoElement.paused || (!remoteMicMuted && !isLocallyMuted && videoElement.muted)) {
                console.log(logPrefix, `[AttachLogic] Video was paused or needs unmuting, attempting to play again.`);
                videoElement.play().catch(e => {
                    console.error(logPrefix, `[AttachLogic] Retry play failed:`, e);
                    if (e.name === 'NotAllowedError' || e.name === 'AbortError') {
                        console.log(logPrefix, "[AttachLogic] Retry play failed, setting needsInteraction=true");
                        setNeedsInteraction(true);
                        setIsLocallyMuted(true);
                    }
                });
            }
            applyVideoQuality(VideoQuality.HIGH, videoPublicationToAttach);
        }
    } else {
        // No suitable video track found to attach
        console.log(logPrefix, "[AttachLogic] No SUITABLE owner video track found (ScreenShare or Camera that is subscribed AND has a track object).");
        if (videoElement.srcObject) { // Check srcObject for detachment
            console.log(logPrefix, `[AttachLogic] Detaching previous stream as no suitable video track is available.`);
             // Stop tracks before clearing
            (videoElement.srcObject as MediaStream)?.getTracks().forEach(track => track.stop());
            videoElement.pause();
            videoElement.srcObject = null;
            setAttachedTrackSid(null);
            attachedPublicationRef.current = null;
        } else {
            console.log(logPrefix, "[AttachLogic] No stream to detach, video element already clear or never attached.");
        }
    }
  }, [room, ownerAddress, logPrefix, desiredQuality, applyVideoQuality, volume, remoteMicMuted, isLocallyMuted]); // Added volume and remoteMicMuted dependencies

  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    // --- Lógica para el Dueño (Preview/Live Local) ---
    if (isOwner) {
      if (localStream) {
        if (videoElement.srcObject !== localStream) {
          console.log(logPrefix, "Attaching owner's local stream.");
          // Detach any remote track first if switching from viewer to owner somehow
          if (attachedPublicationRef.current) {
              attachedPublicationRef.current.track?.detach(videoElement);
              attachedPublicationRef.current = null;
          }
          videoElement.srcObject = localStream;
          videoElement.volume = 0; // Owner preview should have 0 volume locally
          videoElement.muted = true; // Owner preview is always muted
          videoElement.playsInline = true;
          videoElement.load(); // Explicitly load
          videoElement.play().catch(e => console.error(logPrefix, 'Owner local play failed:', e));
          setAttachedTrackSid('local');
        } else if (videoElement.paused) {
            // If srcObject is already set but paused, try playing again
            videoElement.play().catch(e => console.error(logPrefix, 'Owner local retry play failed:', e));
        }
      } else {
        if (videoElement.srcObject) {
            console.log(logPrefix, "Owner, but no local stream. Clearing video.");
            videoElement.pause();
            videoElement.srcObject = null;
            setAttachedTrackSid(null);
            attachedPublicationRef.current = null;
        }
      }
      setNeedsInteraction(false); // Owner preview doesn't need interaction overlay
      setIsLocallyMuted(true); // Owner preview is always muted locally
      return; // Salir temprano para el dueño
    }

    // --- Lógica para Espectadores ---

    // --- Separated Effect for Playback State Synchronization ---
    // This effect ONLY handles play/pause/mute based on state, not attachment.
    const shouldBeMuted = remoteMicMuted || isLocallyMuted || needsInteraction;
    // --- ADDED: Log volume/mute application ---
    console.log(logPrefix, `[ViewerEffect Sync Mute] Applying volume: ${volume} and muted state: ${shouldBeMuted} (remote: ${remoteMicMuted}, local: ${isLocallyMuted}, interaction: ${needsInteraction})`);
    // --- END ADDED ---
    videoElement.volume = volume; // Ensure volume is always synced
    if (videoElement.muted !== shouldBeMuted) {
        console.log(logPrefix, `[ViewerEffect Sync Mute] Updating video muted from ${videoElement.muted} to ${shouldBeMuted} (remote: ${remoteMicMuted}, local: ${isLocallyMuted}, interaction: ${needsInteraction})`);
        videoElement.muted = shouldBeMuted;
    }

    // Attempt to play ONLY if conditions are met AND video is currently paused
    if (!shouldBeMuted && videoElement.paused) {
        // --- ADDED: Detailed log before play attempt ---
        console.log(logPrefix, `[ViewerEffect Sync Mute] Conditions met to play. Pre-play check: readyState=${videoElement.readyState}, networkState=${videoElement.networkState}`);
        if (videoElement.srcObject instanceof MediaStream) {
            const stream = videoElement.srcObject;
            const audioTracks = stream.getAudioTracks();
            const videoTracks = stream.getVideoTracks();
             console.log(logPrefix, `[ViewerEffect Sync Mute] Pre-play srcObject check: AudioTracks=${audioTracks.length}, VideoTracks=${videoTracks.length}. IDs: Audio=[${audioTracks.map(t=>t.id).join(', ')}], Video=[${videoTracks.map(t=>t.id).join(', ')}]`);
        } else {
             console.log(logPrefix, `[ViewerEffect Sync Mute] Pre-play srcObject check: srcObject is NOT a MediaStream.`);
        }
        // --- END ADDED ---

        console.log(logPrefix, "[ViewerEffect Sync Mute] Attempting play...");
        let playInitiated = true;
        videoElement.play().then(() => {
            console.log(logPrefix, "[ViewerEffect Sync Mute] Playback successfully started/resumed.");
        }).catch(e => {
            // --- MODIFICATION START ---
            // Only set needsInteraction if play was genuinely blocked AND it's NOT the initial NotAllowedError,
            // or if it's a different error type.
            // Let explicit user actions (mute toggle, volume slider) handle interaction needs primarily.
            if (playInitiated && e.name !== 'AbortError') { // Check if play was initiated by this effect run and not aborted
                 console.error(logPrefix, "[ViewerEffect Sync Mute] Failed to play video:", e);
                 if (e.name === 'NotAllowedError') {
                     console.log(logPrefix, "[ViewerEffect Sync Mute] Play failed due to browser policy (NotAllowedError). Allowing potential recovery without immediate interaction requirement.");
                     // setNeedsInteraction(true); // REMOVED
                     // setIsLocallyMuted(true); // REMOVED
                 } else {
                     // For other errors, maybe still require interaction? Or just log.
                     console.warn(logPrefix, "[ViewerEffect Sync Mute] Play failed due to non-policy error. Consider if interaction is needed.");
                     // setNeedsInteraction(true); // Optional: uncomment if other errors should trigger interaction
                     // setIsLocallyMuted(true); // Optional: uncomment if other errors should trigger interaction
                 }
            } else if (e.name === 'AbortError') {
                 // Ignore AbortError here as it might be due to track re-attachment or cleanup.
                 console.log(logPrefix, "[ViewerEffect Sync Mute] Playback aborted, likely due to track change or cleanup.");
            }
            // --- MODIFICATION END ---
        });
        // Ensure flag is reset if attach logic runs
        playInitiated = false;  // <-- opcional reset sin salir del efecto
    }
    // --- End Separated Effect Logic ---


    // --- Effect for Attaching Tracks and Listeners (Reduced Dependencies) ---
    if (!isOwner && room && ownerAddress) {
      console.log(logPrefix, "[ViewerEffect Attach] Setting up listeners and initial track check.");

      // Initial check - Call attach logic
      findAndAttachOwnerVideoTrack();

      // --- REVISED Listeners for viewers ---
      const handleOwnerTrackUpdate = (
          arg1: RemoteTrack | RemoteTrackPublication | Participant, // Could be track, pub, or participant depending on event
          arg2?: RemoteTrackPublication | Participant, // Could be pub or participant
          arg3?: Participant // Could be participant
      ) => {
          let eventParticipant: Participant | undefined = undefined;
          let eventTrackKind: Track.Kind | undefined = undefined;
          let eventTrackSid: string | undefined = undefined;
          let eventSource: Track.Source | undefined = undefined;
          let eventType: string = 'unknown'; // For logging
          let shouldReEvaluate = false; // Flag to trigger re-evaluation

          // Determine the participant and track info based on the event signature
          if (arg1 instanceof RemoteTrack && arg2 instanceof RemoteTrackPublication && arg3 instanceof Participant) { // TrackSubscribed/Unsubscribed
              eventParticipant = arg3;
              eventTrackKind = arg1.kind;
              eventTrackSid = arg1.sid;
              eventSource = arg2.source;
              eventType = arg1.mediaStreamTrack ? 'TrackSubscribed' : 'TrackUnsubscribed';
              // --- ADDED LOGGING ---
              if (eventType === 'TrackSubscribed' && eventTrackKind === Track.Kind.Video && eventParticipant?.identity === ownerAddress) {
                  console.log(logPrefix, `[ViewerEffect Revised] >>>>> TrackSubscribed event for OWNER VIDEO track detected! SID: ${eventTrackSid}, Source: ${eventSource}, Track Ready: ${!!arg1.mediaStreamTrack} <<<<<`);
                  shouldReEvaluate = true; // Definitely re-evaluate on video subscribe
              } else if (eventType === 'TrackUnsubscribed' && eventTrackKind === Track.Kind.Video && eventParticipant?.identity === ownerAddress) {
                  console.log(logPrefix, `[ViewerEffect Revised] >>>>> TrackUnsubscribed event for OWNER VIDEO track detected! SID: ${eventTrackSid}, Source: ${eventSource} <<<<<`);
                  shouldReEvaluate = true; // Re-evaluate on video unsubscribe
              }
              // --- END ADDED LOGGING ---

          } else if (arg1 instanceof RemoteTrackPublication && arg2 instanceof Participant) { // TrackPublished/Unpublished/Muted/Unmuted
              eventParticipant = arg2;
              eventTrackKind = arg1.kind;
              eventTrackSid = arg1.trackSid;
              eventSource = arg1.source;
              // Differentiate based on publication state
              if (arg1.isSubscribed && arg1.track) {
                  eventType = arg1.isMuted ? 'TrackMuted' : 'TrackUnmuted';
              } else if (arg1.track === undefined && !arg1.isSubscribed) {
                  // This often indicates an unpublish event
                  eventType = 'TrackUnpublished';
              } else if (arg1.track !== undefined && !arg1.isSubscribed) {
                  // This might indicate a publish event before subscription completes
                  eventType = 'TrackPublished';
              } else {
                  eventType = 'PublicationStateChange'; // Catch-all for other changes
              }

              // Re-evaluate on publish/unpublish of video tracks
              if (eventTrackKind === Track.Kind.Video && (eventType === 'TrackPublished' || eventType === 'TrackUnpublished')) {
                  shouldReEvaluate = true;
              }
              // Also re-evaluate on mute/unmute as it might affect visibility/state display
              if (eventTrackKind === Track.Kind.Video && (eventType === 'TrackMuted' || eventType === 'TrackUnmuted')) {
                  shouldReEvaluate = true; // Might need to update icons or state
              }

              // <<< NUEVO: re-evaluar también cuando cambie audio del owner >>>
              if (eventTrackKind === Track.Kind.Audio
                  && (eventType === 'TrackSubscribed' || eventType === 'TrackUnsubscribed' || eventType === 'TrackMuted' || eventType === 'TrackUnmuted')
                  && eventParticipant?.identity === ownerAddress) 
              {
                  console.log(logPrefix, `[ViewerEffect Revised] Audio track event for OWNER detected (${eventType}). Triggering re-attach.`);
                  shouldReEvaluate = true;
              }
              // <<< fin cambio >>>

          } else if (arg1 instanceof Participant) { // ParticipantConnected/Disconnected/MetadataChanged etc.
              eventParticipant = arg1;
              eventType = 'ParticipantEvent';
              // Re-evaluate if the owner connects/disconnects
              if (eventParticipant.identity === ownerAddress) {
                  shouldReEvaluate = true;
              }
          }
          // Add more checks if listening to other events

          // Now check if the event is relevant and requires re-evaluation
          if (shouldReEvaluate && eventParticipant && eventParticipant.identity === ownerAddress) {
              console.log(logPrefix, `[ViewerEffect Revised] Owner (${ownerAddress}) event requires re-evaluation. Type: ${eventType}, Kind: ${eventTrackKind ?? 'N/A'}, SID: ${eventTrackSid ?? 'N/A'}, Source: ${eventSource ?? 'N/A'}.`);
              // Use a small delay for TrackSubscribed to allow the track object to potentially become fully available
              if (eventType === 'TrackSubscribed' && eventTrackKind === Track.Kind.Video) {
                  console.log(logPrefix, `[ViewerEffect Revised] Applying small delay before re-evaluation for TrackSubscribed.`);
                  setTimeout(() => {
                      console.log(logPrefix, `[ViewerEffect Revised] Running findAndAttachOwnerVideoTrack after delay.`);
                      findAndAttachOwnerVideoTrack(); // Call stable callback
                  }, 150); // 150ms delay, adjust if needed
              } else {
                  findAndAttachOwnerVideoTrack(); // Call stable callback immediately for other events
              }
          } else if (eventParticipant && eventParticipant.identity !== ownerAddress) {
              // Log if the event was from another participant (for debugging)
              // console.log(logPrefix, `[ViewerEffect Revised] Ignoring event from non-owner participant: ${eventParticipant.identity}`);
          } else if (shouldReEvaluate) {
              // Log if re-evaluation was flagged but participant wasn't owner (shouldn't happen with current logic)
              console.warn(logPrefix, `[ViewerEffect Revised] Re-evaluation flagged but participant was not owner or undefined. EventType: ${eventType}`);
          }
      };
      // --- END REVISED Listeners ---

      // Listen to relevant events
      room.on(RoomEvent.TrackPublished, handleOwnerTrackUpdate);
      room.on(RoomEvent.TrackUnpublished, handleOwnerTrackUpdate);
      room.on(RoomEvent.TrackSubscribed, handleOwnerTrackUpdate);
      room.on(RoomEvent.TrackUnsubscribed, handleOwnerTrackUpdate);
      room.on(RoomEvent.TrackMuted, handleOwnerTrackUpdate);
      room.on(RoomEvent.TrackUnmuted, handleOwnerTrackUpdate);

      // Cleanup function
      return () => {
        console.log(logPrefix, "[ViewerEffect Attach] Cleanup: Removing listeners.");
        room.off(RoomEvent.TrackPublished, handleOwnerTrackUpdate);
        room.off(RoomEvent.TrackUnpublished, handleOwnerTrackUpdate);
        room.off(RoomEvent.TrackSubscribed, handleOwnerTrackUpdate);
        room.off(RoomEvent.TrackUnsubscribed, handleOwnerTrackUpdate);
        room.off(RoomEvent.TrackMuted, handleOwnerTrackUpdate);
        room.off(RoomEvent.TrackUnmuted, handleOwnerTrackUpdate);

        // --- REMOVED Stream Detachment from this cleanup ---
        // The findAndAttachOwnerVideoTrack function handles detaching when necessary
        // during track switches or when conditions are unmet.
        // Detachment on component unmount or room disconnect is handled below.
        // --- ---
      };
    } else if (!isOwner && !room) {
      // Clear video if room becomes null for viewer
      if (videoElement && (videoElement.srcObject || attachedTrackSid)) { // Check srcObject
          console.log(logPrefix, "[ViewerEffect Attach] Room is null. Clearing video.");
          // Stop tracks before clearing
          (videoElement.srcObject as MediaStream)?.getTracks().forEach(track => track.stop());
          videoElement.pause();
          videoElement.srcObject = null;
          setAttachedTrackSid(null);
          attachedPublicationRef.current = null;
      }
      // --- ADDED: Cleanup function for when room becomes null or component unmounts while viewer ---
      return () => {
          if (!isOwner && videoElement && videoElement.srcObject) {
              console.log(logPrefix, "[ViewerEffect Attach Cleanup - No Room/Unmount] Detaching stream.");
              (videoElement.srcObject as MediaStream)?.getTracks().forEach(track => track.stop());
              videoElement.pause();
              videoElement.srcObject = null;
              setAttachedTrackSid(null);
              attachedPublicationRef.current = null;
          }
      }
      // --- ---
    }
    // --- End Effect for Attaching Tracks ---

  // --- MODIFIED Dependency Array ---
  // Effect for Playback Sync depends on mute/interaction states
  // Effect for Attachment/Listeners depends on room/owner/identity changes
  }, [
      room, // For setup and cleanup
      isOwner, // To differentiate logic paths
      localStream, // For owner logic
      logPrefix, // For logging
      ownerAddress, // To identify owner participant and in listeners
      findAndAttachOwnerVideoTrack, // Stable callback for attaching
      // States controlling playback:
      remoteMicMuted,
      isLocallyMuted,
      needsInteraction,
      volume // <-- ADD volume dependency here to ensure volume is applied on change
  ]);
  // --- END MODIFIED Dependency Array ---

  useEffect(() => {
    if (!liveStartTime) return;
    const interval = setInterval(() => {
      setElapsed(Date.now() - liveStartTime);
    }, 1000);
    return () => clearInterval(interval);
  }, [liveStartTime]);

  // Añadir captura de la pista de audio
  useEffect(() => {
    // Para espectadores: obtener pista de audio del stream remoto
    if (!isOwner && videoRef.current?.srcObject instanceof MediaStream) {
      const stream = videoRef.current.srcObject;
      const audioTracks = stream.getAudioTracks();
      
      if (audioTracks.length > 0) {
        setAudioTrack(audioTracks[0]);
      } else {
        setAudioTrack(null);
      }
    } 
    // NUEVO: Para el propietario: obtener pista de audio del stream local
    else if (isOwner && localStream) {
      const audioTracks = localStream.getAudioTracks();
      
      if (audioTracks.length > 0) {
        setAudioTrack(audioTracks[0]);
      } else {
        setAudioTrack(null);
      }
    } 
    else {
      setAudioTrack(null);
    }
  }, [isOwner, attachedTrackSid, remoteMicMuted, localStream]); // Añadir localStream como dependencia

  const handleVolumeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(event.target.value);
    setVolume(newVolume);
    const videoElement = videoRef.current;
    if (videoElement) {
      videoElement.volume = newVolume;
      // If user increases volume, assume they want it unmuted (locally)
      if (newVolume > 0 && (isLocallyMuted || needsInteraction) && !remoteMicMuted) {
          console.log(logPrefix, "[handleVolumeChange] User interaction via slider. Attempting unmute & play.");
          setIsLocallyMuted(false); // Set local mute state to false
          setNeedsInteraction(false); // Assume interaction is handled
          // The useEffect will handle the actual videoElement.muted and play() call
      } else if (newVolume === 0 && !isLocallyMuted) {
          // If user sets volume to 0, mute locally
          console.log(logPrefix, "[handleVolumeChange] User set volume to 0. Muting locally.");
          setIsLocallyMuted(true);
      }
    }
  };

  // --- MODIFIED: Handler for the interaction button ---
  const handleInteractionClick = () => {
    const videoElement = videoRef.current;
    // --- MODIFICATION START: Check needsInteraction state ---
    // Only proceed if interaction is actually needed and remote isn't muted
    if (videoElement && needsInteraction && !remoteMicMuted) {
    // --- MODIFICATION END ---
        console.log(logPrefix, "[handleInteractionClick] User clicked interaction button.");
        // Set state first
        setNeedsInteraction(false);
        setIsLocallyMuted(false);

        // --- MODIFIED: Direct play attempt within user interaction context ---
        console.log(logPrefix, "[handleInteractionClick] Attempting video.play() directly.");
        videoElement.play().then(() => {
            console.log(logPrefix, "[handleInteractionClick] video.play() successful.");
            // Ensure volume and mute state are correct AFTER successful play
            videoElement.volume = volume; // Re-apply volume state
            videoElement.muted = false; // Explicitly unmute after successful play initiated by user

            // --- ADDED: Log final state after successful play ---
            const audioTracks = videoElement.srcObject instanceof MediaStream ? videoElement.srcObject.getAudioTracks() : [];
            const videoTracks = videoElement.srcObject instanceof MediaStream ? videoElement.srcObject.getVideoTracks() : []; // Also log video tracks
            console.log(logPrefix, `[handleInteractionClick] Post-play state: muted=${videoElement.muted}, volume=${videoElement.volume}, srcObjectAudioTracks=${audioTracks.length}, srcObjectVideoTracks=${videoTracks.length}`);
            // --- END ADDED ---

        }).catch(e => {
            console.error(logPrefix, "[handleInteractionClick] video.play() failed:", e);
            // If play fails even here, something else might be wrong.
            // Re-set needsInteraction? Or maybe alert the user?
            setNeedsInteraction(true); // Re-set if play fails
            setIsLocallyMuted(true);
        });
        // --- END MODIFIED ---

    } else {
        console.log(logPrefix, "[handleInteractionClick] Click ignored (remote muted or no video element).");
    }
  };
  // --- ---

  // --- NEW: Handler for the Mute Toggle Button ---
  const handleToggleMute = () => {
      if (!remoteMicMuted) { // Prevent local unmute if remote is muted
          const newMutedState = !isLocallyMuted;
          console.log(logPrefix, `[handleToggleMute] Toggling local mute to: ${newMutedState}`);
          setIsLocallyMuted(newMutedState);
          if (!newMutedState) { // If unmuting
              setNeedsInteraction(false); // Assume interaction is handled
          }
          // The useEffect will handle the actual videoElement.muted and play() call
      } else {
          console.log(logPrefix, "[handleToggleMute] Cannot unmute locally, remote streamer is muted.");
      }
  };
  // --- ---

  // Modificar el manejo de cambio de calidad para forzar siempre HIGH al cambiarla manualmente
  const handleQualityChange = (quality: VideoQuality) => {
    setDesiredQuality(quality); // Update state
    setShowQualityMenu(false); // Close menu
    
    // Siempre forzar HIGH sin importar qué calidad pidió el usuario
    // pues estamos priorizando calidad sobre rendimiento
    applyVideoQuality(VideoQuality.HIGH, attachedPublicationRef.current);
    console.log(logPrefix, `Usuario seleccionó ${quality} pero forzamos HIGH para mejor experiencia`);
  };

  // Nuevo manejador para el botón del ecualizador
  const handleEqButtonClick = () => {
    setShowEqualizer(prev => !prev);
  };

  // Handler to save equalizer settings
  const handleSaveEqualizerSettings = (bands: EqualizerBand[]) => {
    setEqualizerSettings(bands);
  };

  // --- Renderizado ---
  const showVideoContent = (isOwner && localStream) || (!isOwner && attachedTrackSid && attachedTrackSid !== 'local');
  const currentMutedState = isOwner || remoteMicMuted || isLocallyMuted || needsInteraction;
  const shouldShowVolumeControls = !isOwner && showVideoContent;

  // --- MODIFIED: Simplified log ---
  console.log(`${logPrefix} [Volume Controls Check] Should Show: ${shouldShowVolumeControls} (!isOwner: ${!isOwner}, showVideoContent: ${showVideoContent})`);
  // --- ---

  // --- ADDED: Log just before return ---
  console.log(`${logPrefix} [Pre-Return Check] isOwner=${isOwner}, showVideoContent=${showVideoContent}, shouldShowVolumeControls=${shouldShowVolumeControls}`);
  // --- ---

  return (
    <div className="relative max-w-[700px] bg-black rounded-[10px] overflow-hidden">

      <video
        ref={videoRef}
        autoPlay
        playsInline
        // Muted state now primarily controlled by useEffect synchronization
        // muted={currentMutedState} // We let the useEffect handle this directly
        style={{ width: '100%', height: 'auto', display: showVideoContent ? 'block' : 'none' }}
        className="rounded-t-[10px] mb-0 bg-black"
      />
      <audio ref={audioRef} autoPlay hidden />

      {/* Overlay play button when interaction is needed */}
      {needsInteraction && showVideoContent && (
        <div className="absolute inset-0 flex items-center justify-center z-40">
          <button
            onClick={handleInteractionClick}
            className="p-4 bg-black bg-opacity-50 rounded-full"
          >
            <FaPlay className="text-white text-3xl" />
          </button>
        </div>
      )}

      {/* Improved reaction overlay positioning - move outside video boundaries */}
      {room && (
        <div 
          className="absolute inset-0 z-50 pointer-events-none overflow-visible" 
          style={{ zIndex: 99999 }}
        >
          <ReactionOverlay
            room={room}
            showButtons={false}
            showBubbles={true}
          />
        </div>
      )}

      {/* --- Settings (quality) top-left --- */}
      {!isOwner && attachedPublicationRef.current?.kind===Track.Kind.Video && (
        <div className="absolute top-2 left-2 z-20">
          <button onClick={()=>setShowQualityMenu(!showQualityMenu)} className="p-1 bg-black bg-opacity-50 text-white rounded">
            <FaCog />
          </button>
          {showQualityMenu && (
            <div className="absolute mt-1 w-28 bg-black bg-opacity-80 text-white rounded shadow-lg py-1 text-sm">
              <button
                onClick={() => handleQualityChange(VideoQuality.HIGH)}
                className={`block w-full text-left px-3 py-1 hover:bg-gray-700 ${desiredQuality === VideoQuality.HIGH ? 'font-bold' : ''}`}
              >
                High
              </button>
              <button
                onClick={() => handleQualityChange(VideoQuality.MEDIUM)}
                className={`block w-full text-left px-3 py-1 hover:bg-gray-700 ${desiredQuality === VideoQuality.MEDIUM ? 'font-bold' : ''}`}
              >
                Medium
              </button>
              <button
                onClick={() => handleQualityChange(VideoQuality.LOW)}
                className={`block w-full text-left px-3 py-1 hover:bg-gray-700 ${desiredQuality === VideoQuality.LOW ? 'font-bold' : ''}`}
              >
                Low
              </button>
            </div>
          )}
        </div>
      )}

      {/* --- Viewer count top-right --- */}
      {room && (
        <div className="absolute top-2 right-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded z-20">
          👤 {viewerCount}
        </div>
      )}

      {/* --- Timer & mic/cam bottom-left --- */}
      {!isOwner && showVideoContent && (
        <>
        {/*
          <div className="absolute bottom-2 left-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded z-20">
            { new Date(elapsed).toISOString().substr(11,8) }
            { estimatedDuration && ` / ${new Date(estimatedDuration).toISOString().substr(11,8)}` }
          </div>
          */}
          <div className="absolute bottom-2 left-3 flex items-center gap-1 text-white z-20 bg-black bg-opacity-30 p-1 rounded">
            {remoteMicMuted ? <FaMicrophoneSlash/> : <FaMicrophone/>}
            {remoteCamOff ? <FaVideoSlash/> : <FaVideo/>}
          </div>
        </>
      )}
      
      {/* NUEVO: Visualizador de ondas de audio en el centro inferior */}
      {showVideoContent && audioTrack && (
        <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 z-20">
          <AudioWaveform 
            audioTrack={audioTrack} 
            isAudioMuted={isOwner ? isMicMuted : (remoteMicMuted || isLocallyMuted)}
            isOwner={isOwner} // Pasar la propiedad isOwner
            onEqButtonClick={handleEqButtonClick} // Pasar el manejador del botón
          />
        </div>
      )}

      {/* NUEVO: Modal del ecualizador */}
      {isOwner && showEqualizer && audioTrack && (
        <AudioEqualizer 
          audioTrack={audioTrack}
          onClose={() => setShowEqualizer(false)}
          savedBands={equalizerSettings}
          onSaveSettings={handleSaveEqualizerSettings}
        />
      )}

      {/* --- Volume controls bottom-right --- */}
      {shouldShowVolumeControls && (
        <div className="absolute bottom-2 right-2 flex items-center gap-2 z-20">
          <button onClick={handleToggleMute} disabled={remoteMicMuted} className="p-1 text-white">
            {currentMutedState ? <FaVolumeMute/> : <FaVolumeUp/>}
          </button>
          <input
            type="range" min="0" max="1" step="0.05"
            value={currentMutedState?0:volume}
            onChange={handleVolumeChange}
            disabled={remoteMicMuted}
            className="w-24 h-2 bg-gray-700 rounded cursor-pointer accent-[#45c0be]"
          />
        </div>
      )}

      {/* --- Watermark --- */}
      {showVideoContent && (
        <img
          src={watermarkGif}
          alt="Watermark"
          className="absolute right-2 top-1/2 transform -translate-y-1/2 w-24 opacity-90 pointer-events-none z-30"
          // Para un tamaño custom, puedes usar:
          // style={{ width: '64px', height: '64px' }}
        />
      )}

    </div>
  );
};

export default StreamPlayer;